import React, { useMemo } from 'react';
import { useLocalStorage } from 'react-use';
import { keyBy, isFunction, fromPairs, sortBy } from 'lodash';
import { addDays, startOfWeek, format as formatDate } from 'date-fns';
import { collection, query, } from 'firebase/firestore';
import classnames from 'classnames';
import numeral from 'numeral';

import useQueryParams from '../hooks/useQueryParams';
import useTenant from '../hooks/useTenant';
import TenantPage from '../TenantPage';
import QuerySelector from '../QuerySelector';
import QueryWeekSelector from '../QueryWeekSelector';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import PlTabs from '../PlTabs';
import NumberDiscrepancyHelpButton from '../NumberDiscrepancyHelpButton';
import QueryBoolean from '../QueryBoolean';
import ExportButton from '../ExportButton';
import MallBadge from '../MallBadge';
import { mapKeysToJa, jas } from '../../shared/texts';
import usePlCalculation from '../hooks/usePlCalculation';

export default function DailyPL (props) {
  return (
    <TenantPage>
      <PageContent {...props} />
    </TenantPage>
  );
};

function PageContent (props) {
  const { tenant } = props;
  const queryParams = useQueryParams();
  const defaultWeekStart = startOfWeek(addDays(new Date(), -1), { weekStartsOn: 1 });
  const weekStartDate = queryParams.weekStartDate ? new Date(queryParams.weekStartDate) : defaultWeekStart;

  const sellers = useCollectionSubscription(query(collection(tenant.ref, 'sellers')));
  const shops = useCollectionSubscription(query(collection(tenant.ref, 'shops')));
  const isOwnerSelected = !!queryParams.sellerId;
  const selectedSellers = useMemo(_ => isOwnerSelected ? sellers.filter(_ => _.id === queryParams.sellerId) : sellers, [queryParams.sellerId, sellers]);
  const selectedShops = useMemo(_ => isOwnerSelected ? shops.filter(_ => _.id === queryParams.sellerId) : shops, [queryParams.sellerId, shops]);
  const sellerOptions = sortBy([
    ...sellers.map(_ => ({ label: _.name, value: _.id, index: _.index })),
    ...shops.map(_ => ({ label: _.name, value: _.id, index: _.index })),
  ], _ => _.index ?? Infinity);

  // テナント設定「税抜き表示のデフォルト」をデフォルトに。URLクエリ指定があればそちら優先
  const defaultExcludeTax = tenant.exportExcludeTax === 'excluded';
  const isExcludeTax = queryParams.excludeTax != null ? queryParams.excludeTax === '1' : defaultExcludeTax;
  const dateRangeType = 'weekly';
  const dateCount = 7;
  const endDate = addDays(weekStartDate, 6);
  
  // 折りたたみ状態の管理
  const [collapsed = {}, setCollapsed] = useLocalStorage('dailyPlCollapsed', {});

  const toggleCollapse = (key) => {
    setCollapsed({ ...collapsed, [key]: !collapsed[key] });
  };
  
  // PL計算データを取得（日別データも含む）
  const { items: totalItems, salesAmount: totalSalesAmount, dailyData } = usePlCalculation({
    dateRange: [weekStartDate, endDate],
    sellers: selectedSellers,
    shops: selectedShops,
    dateRangeType,
    isExcludeTax,
  });

  // 日付配列を生成
  const dates = useMemo(_ => 
    Array.from({ length: dateCount }, (_, i) => addDays(weekStartDate, i))
  , [weekStartDate, dateCount]);

  // エクスポート用のデータ生成関数
  const getExportData = () => {
    return itemStructure.flatMap(itemConfig => {
      const { key, hasChildren, children } = itemConfig;
      
      // 親項目
      const parentRow = mapKeysToJa({ 
        itemName: jas[key] || key, 
        ...fromPairs((dailyData || [{ items: totalItems, salesAmount: totalSalesAmount }]).map((dayData, index) => [
          formatDate(dates[index], 'yyyy/MM/dd'),
          dayData.items[key]?.computeValue() || 0
        ])),
        weekTotal: totalItems[key]?.computeValue() || 0
      });
      
      // 子項目
      const childRows = hasChildren && children ? children.map(childKey => 
        mapKeysToJa({ 
          itemName: `  ${jas[childKey] || childKey}`, 
          ...fromPairs((dailyData || [{ items: totalItems, salesAmount: totalSalesAmount }]).map((dayData, index) => [
            formatDate(dates[index], 'yyyy/MM/dd'),
            dayData.items[key]?.children?.[childKey]?.computeValue() || 0
          ])),
          weekTotal: totalItems[key]?.children?.[childKey]?.computeValue() || 0
        })
      ) : [];
      
      return [parentRow, ...childRows];
    });
  };

  // 階層構造を持つ項目定義
  const itemStructure = useMemo(() => {
    const baseItems = [
      { key: 'sales', hasChildren: false },
      { key: 'refund', hasChildren: false },
      {
        key: 'cods',
        hasChildren: true,
        children: Object.keys(totalItems.cods?.children || {})
      },
      { key: 'grossProfit', hasChildren: false },
      { 
        key: 'cods2',
        hasChildren: true,
        children: Object.keys(totalItems.cods2?.children || {})
      },
      { key: 'grossProfit2', hasChildren: false },
      { 
        key: 'variableSga',
        hasChildren: true,
        children: Object.keys(totalItems.variableSga?.children || {})
      },
      { key: 'contributionMargin', hasChildren: false },
      { key: 'operatingProfit', hasChildren: false }
    ];
    return baseItems;
  }, [totalItems]);

  // 行をレンダリングする関数
  const renderRow = (itemConfig, isChild = false, parentKey = null) => {
    const { key, hasChildren, children } = itemConfig;
    
    const item = isChild && parentKey ? 
      totalItems[parentKey]?.children?.[key] : 
      totalItems[key];
    
    if (!item) return null;
    
    const { isComputed, label } = item;
    const isCollapsed = collapsed[key];
    
    const parentRow = (
      <tr key={key} className={classnames(
        { 'table-light': !isChild },
        { 'bg-white': isChild }
      )}>
        <td className={classnames({ 'fw-bold': isComputed })} style={{ verticalAlign: 'middle' }}>
          <div className="d-flex align-items-center">
            {isChild && <span className="me-2">&nbsp;&nbsp;</span>}
            {hasChildren && (
              <button
                className="btn p-0 me-2 border-0 bg-transparent text-muted"
                onClick={() => toggleCollapse(key)}
                style={{ 
                  fontSize: '10px', 
                  lineHeight: 1,
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={isCollapsed ? '展開' : '折りたたみ'}
              >
                {isCollapsed ? '▶' : '▼'}
              </button>
            )}
            <span>{isFunction(label) ? label() : label}</span>
            <MallBadge feeKey={key} />
          </div>
        </td>
        {(dailyData || [{ items: totalItems, salesAmount: totalSalesAmount }]).map((dayData, index) => {
          const itemToUse = isChild && parentKey ? 
            dayData.items[parentKey]?.children?.[key] : 
            dayData.items[key];
          const value = itemToUse?.computeValue() || 0;
          const rate = dayData.salesAmount > 0 ? Math.abs(value) / dayData.salesAmount : 0;
          const dateStr = formatDate(dates[index], 'yyyy-MM-dd');
          
          return (
            <td key={dateStr} className="text-end" style={{ verticalAlign: 'middle' }}>
              <div className={classnames('fw-semibold', { 'text-danger': value < 0 })} style={{ lineHeight: 1.2 }}>
                {numeral(value).format()}円
              </div>
              <div className="text-muted small" style={{ lineHeight: 1.1, marginTop: '2px' }}>
                {dayData.salesAmount > 0 ? numeral(rate).format('0.0%') : '-'}
              </div>
            </td>
          );
        })}
        <td className="text-end" style={{ verticalAlign: 'middle' }}>
          {(() => {
            const totalValue = item?.computeValue() || 0;
            const totalRate = totalSalesAmount > 0 ? Math.abs(totalValue) / totalSalesAmount : 0;
            
            return (
              <>
                <div className={classnames('fw-bold', { 'text-danger': totalValue < 0 })} style={{ lineHeight: 1.2 }}>
                  {numeral(totalValue).format()}円
                </div>
                <div className="text-muted small" style={{ lineHeight: 1.1, marginTop: '2px' }}>
                  {totalSalesAmount > 0 ? numeral(totalRate).format('0.0%') : '-'}
                </div>
              </>
            );
          })()}
        </td>
      </tr>
    );
    
    if (hasChildren && !isCollapsed && children && children.length > 0) {
      const childRows = children.flatMap(childKey => 
        renderRow({ key: childKey, hasChildren: false }, true, key)
      );
      return [parentRow, ...childRows];
    }
    
    return [parentRow];
  };

  return (
    <div className="daily-pl container-fluid">
      <div className="px-4">
        <div className="row">
          <div className="col-12">
            <div className="card p-4">
              <PlTabs activeTab="daily" />
              <div className="mb-4 d-flex gap-2 align-items-end justify-content-start">
                <QueryWeekSelector paramName="weekStartDate" style={{ width: 220 }} defaultValue={weekStartDate} />
                {sellerOptions.length >= 2 && <QuerySelector label="ショップ" paramName="sellerId" options={sellerOptions} style={{ width: 180 }} />}
                <QueryBoolean paramName="excludeTax" label="税抜き" defaultValue={defaultExcludeTax ? '1' : '0'} />
                <ExportButton 
                  label={false}
                  outline
                  fileName={`日次PL_${formatDate(weekStartDate, 'yyyy-MM-dd')}.csv`}
                  rows={getExportData}
                />
                <div className="ms-auto">
                  <NumberDiscrepancyHelpButton sellers={sellers} shops={shops} />
                </div>
              </div>
              <div className="container-fluid px-0">
                <div className="table-responsive" style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th style={{ minWidth: '140px', width: '140px' }} className="fw-bold sticky-col"></th>
                        {dates.map(date => (
                          <th key={formatDate(date, 'yyyy-MM-dd')} className="text-center fw-bold" style={{ minWidth: '110px', width: '110px' }}>
                            <div>
                              {formatDate(date, 'M/d')}
                              <span className="text-muted">
                                {' (' + ['日', '月', '火', '水', '木', '金', '土'][date.getDay()] + ')'}
                              </span>
                            </div>
                          </th>
                        ))}
                        <th className="text-center fw-bold" style={{ minWidth: '110px', width: '110px' }}>
                          <div>週合計</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemStructure.flatMap(item => renderRow(item))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
